import React, { useState, useEffect } from "react";
import { C, fmtINR, getAuthHeaders } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";

// ─── helpers ──────────────────────────────────────────────────────────────────
function heatColor(val) {
  if (val == null) return "#f9fafb";
  if (val >= 80)   return "#b91c1c";
  if (val >= 60)   return "#dc2626";
  if (val >= 40)   return "#f87171";
  if (val >= 20)   return "#fca5a5";
  return           "#fecaca";
}

// ─── mock data ────────────────────────────────────────────────────────────────
// API: GET /api/ipd-billing/get_reports/?org_code={orgCode}&days={period}
//      Headers: getAuthHeaders()
//      Response: { kpi, trend, departments, insurance, heatmap }

const KPI_CONFIG = [
  { label: "TOTAL REVENUE",          value: "₹14.2 Cr", delta: "+12.4%",   pos: true  },
  { label: "COLLECTION EFFICIENCY",  value: "92.4%",    delta: "+1.8 pts", pos: true  },
  { label: "DAYS IN AR",             value: "38",       delta: "-3 days",  pos: true  },
  { label: "CLAIM REALIZATION",      value: "87.1%",    delta: "-1.2 pts", pos: false },
];

const TREND_DATA = [
  { day: "Mon", lakh: 32 }, { day: "Tue", lakh: 38 },
  { day: "Wed", lakh: 35 }, { day: "Thu", lakh: 52 },
  { day: "Fri", lakh: 48 }, { day: "Sat", lakh: 58 },
  { day: "Sun", lakh: 44 },
];

const DEPT_DATA = [
  { name: "Cardiology",      revenue: 4820000, profit: 2650000 },
  { name: "Orthopedics",     revenue: 3650000, profit: 1900000 },
  { name: "Oncology",        revenue: 3120000, profit: 1560000 },
  { name: "Neurology",       revenue: 2480000, profit: 1120000 },
  { name: "General Surgery", revenue: 2150000, profit:  860000 },
  { name: "Pediatrics",      revenue: 1380000, profit:  490000 },
];

const INSURANCE_DATA = [
  { name: "Star Health",   pct: 24 }, { name: "CGHS",      pct: 18 },
  { name: "Bajaj Allianz", pct: 15 }, { name: "HDFC Ergo", pct: 12 },
  { name: "Niva Bupa",     pct: 10 }, { name: "Others",    pct: 21 },
];

const HEAT_ROWS = ["Cardiology", "Ortho", "Oncology", "Neurology", "Gen. Surgery"];
const HEAT_COLS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HEAT_DATA = [
  [null, 82,   74,   58,   45,   38,   91  ],
  [55,   43,   38,   null, 32,   null, 76  ],
  [67,   88,   null, null, null, null, 44  ],
  [93,   null, null, 68,   null, 54,   62  ],
  [38,   52,   33,   null, null, 48,   29  ],
];

// ─── main component ───────────────────────────────────────────────────────────
export default function IPDBillingReports() {
  const orgCode = sessionStorage.getItem("ORG_CODE") || "";

  const [period, setPeriod]       = useState("30");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    // API: GET /api/ipd-billing/get_reports/?org_code={orgCode}&days={period}
    // Headers: getAuthHeaders()
    // UNCOMMENT WHEN READY:
    // axios.get(`/api/ipd-billing/get_reports/?org_code=${orgCode}&days=${period}`, { headers: getAuthHeaders() })
    //   .then(r => { /* populate state from r.data */ setIsLoading(false); })
    //   .catch(() => setIsLoading(false));
    setTimeout(() => setIsLoading(false), 700);
  }, [period, orgCode]);

  const maxTrend   = Math.max(...TREND_DATA.map(d => d.lakh));
  const maxRevenue = Math.max(...DEPT_DATA.map(d => d.revenue));
  const maxIns     = Math.max(...INSURANCE_DATA.map(d => d.pct));

  return (
    <IPDBillingLayout activeNav="reports">
      <style>{`
        .rpt-export:hover { filter: brightness(1.15); }
        .rpt-period {
          padding: 7px 28px 7px 12px;
          border: 1.5px solid ${C.border};
          border-radius: 8px;
          background: ${C.card};
          color: ${C.text};
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          outline: none;
          -webkit-appearance: none;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236c757d' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 10px center;
        }
      `}</style>

      <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: 22 }}>

        {/* ── page header ──────────────────────────────────────────────────── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>
              Reports &amp; Analytics
            </h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: C.textSub }}>
              {`Executive view · Revenue cycle KPIs · Last ${period} days`}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select
              className="rpt-period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">This year</option>
            </select>
            <button
              className="rpt-export"
              onClick={() => {
                // API: POST /api/ipd-billing/export_report_pack/
                // Body: { org_code: orgCode, days: period }
                // Headers: getAuthHeaders()
                // UNCOMMENT WHEN READY:
                // axios.post("/api/ipd-billing/export_report_pack/", { org_code: orgCode, days: period }, { headers: getAuthHeaders() })
                //   .then(r => { const a=document.createElement("a"); a.href=r.data.url; a.click(); });
                alert("Export pack — integrate with API");
              }}
              style={{
                padding: "8px 18px", border: "none", borderRadius: 8,
                background: C.primary, color: "#fff",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                transition: "filter 0.15s",
              }}
            >
              Export Pack
            </button>
          </div>
        </div>

        {/* ── KPI strip ────────────────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 14 }}>
          {KPI_CONFIG.map((card) => (
            <div
              key={card.label}
              style={{
                flex: 1, padding: "18px 22px",
                background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 600, color: C.textSub, letterSpacing: "0.07em", marginBottom: 10 }}>
                {card.label}
              </div>
              {isLoading ? (
                <div style={{
                  height: 36, width: "65%", borderRadius: 4,
                  background: "linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)",
                  backgroundSize: "200% 100%", animation: "ipdShimmer 1.4s infinite",
                }} />
              ) : (
                <>
                  <div style={{ fontSize: 28, fontWeight: 700, color: C.text, lineHeight: 1, marginBottom: 6 }}>
                    {card.value}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: card.pos ? C.success : C.danger }}>
                    <SvgIcon name={card.pos ? "trending" : "trending-down"} size={13} color={card.pos ? C.success : C.danger} />
                    {card.delta}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* ── two-column main area ─────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>

          {/* Left column (~60%) ─────────────────────────────────────────── */}
          <div style={{ flex: 3, minWidth: 0, display: "flex", flexDirection: "column", gap: 18 }}>

            {/* Revenue Trend bar chart */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 24px" }}>
              <h4 style={{ margin: "0 0 20px", fontSize: 14, fontWeight: 700, color: C.text }}>
                Revenue Trend (₹ Lakh / day)
              </h4>
              {isLoading ? (
                <div style={{
                  height: 200, borderRadius: 8,
                  background: "linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)",
                  backgroundSize: "200% 100%", animation: "ipdShimmer 1.4s infinite",
                }} />
              ) : (
                <div style={{ display: "flex", gap: 10, height: 200, alignItems: "flex-end" }}>
                  {TREND_DATA.map((d) => (
                    <div key={d.day} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                      <div
                        title={`₹${d.lakh}L`}
                        style={{
                          width: "100%",
                          height: `${Math.round((d.lakh / maxTrend) * 100)}%`,
                          background: "linear-gradient(180deg, #14b8a6 0%, #0d9488 100%)",
                          borderRadius: "5px 5px 0 0",
                        }}
                      />
                      <span style={{ fontSize: 12, color: C.textSub }}>{d.day}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Insurance Mix */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 24px" }}>
              <h4 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: C.text }}>
                Insurance Mix
              </h4>
              {isLoading ? (
                <div style={{
                  height: 160, borderRadius: 8,
                  background: "linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)",
                  backgroundSize: "200% 100%", animation: "ipdShimmer 1.4s infinite",
                }} />
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {INSURANCE_DATA.map((ins) => {
                    const barW      = Math.round((ins.pct / maxIns) * 100);
                    const badgeBg   = ins.pct <= 10 ? "#fef3c7" : "#e0f2fe";
                    const badgeClr  = ins.pct <= 10 ? "#b45309" : "#0284c7";
                    return (
                      <div
                        key={ins.name}
                        style={{ padding: "12px 14px", border: `1px solid ${C.border}`, borderRadius: 8, background: "#fafafa" }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{ins.name}</span>
                          <span style={{ padding: "2px 8px", borderRadius: 12, fontSize: 12, fontWeight: 600, background: badgeBg, color: badgeClr }}>
                            {ins.pct}%
                          </span>
                        </div>
                        <div style={{ height: 4, background: "#e9ecef", borderRadius: 2 }}>
                          <div style={{ height: "100%", width: `${barW}%`, background: "#3b82f6", borderRadius: 2 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right column (~40%) ────────────────────────────────────────── */}
          <div style={{ flex: 2, minWidth: 0, display: "flex", flexDirection: "column", gap: 18 }}>

            {/* Department Profitability */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 24px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>Department Profitability</h4>
                <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 11, color: C.textSub }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 12, height: 3, borderRadius: 2, background: "#1d4ed8", display: "inline-block" }} />
                    Revenue
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 12, height: 3, borderRadius: 2, background: "#22c55e", display: "inline-block" }} />
                    Profit
                  </span>
                </div>
              </div>
              {isLoading ? (
                [1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} style={{
                    height: 44, borderRadius: 4, marginBottom: 12,
                    background: "linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)",
                    backgroundSize: "200% 100%", animation: "ipdShimmer 1.4s infinite",
                  }} />
                ))
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {DEPT_DATA.map((d) => {
                    const revW  = Math.round((d.revenue / maxRevenue) * 100);
                    const profW = Math.round((d.profit  / maxRevenue) * 100);
                    return (
                      <div key={d.name}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                          <span style={{ fontSize: 13, color: C.text }}>{d.name}</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{fmtINR(d.revenue)}</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                          <div style={{ height: 5, background: "#e9ecef", borderRadius: 3 }}>
                            <div style={{ height: "100%", width: `${revW}%`, background: "#1d4ed8", borderRadius: 3 }} />
                          </div>
                          <div style={{ height: 5, background: "#e9ecef", borderRadius: 3 }}>
                            <div style={{ height: "100%", width: `${profW}%`, background: "#22c55e", borderRadius: 3 }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Revenue Leakage Heatmap */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 24px" }}>
              <h4 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: C.text }}>
                Revenue Leakage Heatmap
              </h4>

              {isLoading ? (
                <div style={{
                  height: 200, borderRadius: 8,
                  background: "linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)",
                  backgroundSize: "200% 100%", animation: "ipdShimmer 1.4s infinite",
                }} />
              ) : (
                <>
                  {/* column day labels */}
                  <div style={{ display: "flex", gap: 4, marginBottom: 5 }}>
                    <div style={{ width: 72, flexShrink: 0 }} />
                    {HEAT_COLS.map((c) => (
                      <div key={c} style={{ flex: 1, textAlign: "center", fontSize: 10, color: C.textSub, fontWeight: 500 }}>
                        {c}
                      </div>
                    ))}
                  </div>

                  {/* heatmap rows */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    {HEAT_DATA.map((row, ri) => (
                      <div key={ri} style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <div style={{
                          width: 72, flexShrink: 0,
                          fontSize: 11, color: C.textSub,
                          textAlign: "right", paddingRight: 8,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {HEAT_ROWS[ri]}
                        </div>
                        {row.map((val, ci) => (
                          <div
                            key={ci}
                            title={val != null ? `Leakage intensity: ${val}%` : "No data"}
                            style={{
                              flex: 1,
                              aspectRatio: "1",
                              borderRadius: 6,
                              background: heatColor(val),
                              border: val == null ? `1px solid #e9ecef` : "none",
                              cursor: val != null ? "default" : "default",
                            }}
                          />
                        ))}
                      </div>
                    ))}
                  </div>

                  {/* legend */}
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 14, justifyContent: "flex-end" }}>
                    <span style={{ fontSize: 10, color: C.textSub, marginRight: 2 }}>Low</span>
                    {["#fecaca", "#fca5a5", "#f87171", "#dc2626", "#b91c1c"].map((bg) => (
                      <div key={bg} style={{ width: 14, height: 14, borderRadius: 3, background: bg }} />
                    ))}
                    <span style={{ fontSize: 10, color: C.textSub, marginLeft: 2 }}>High</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </IPDBillingLayout>
  );
}
